import React from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RatingScaleSlider from "./RatingScaleSlider";

const DEFAULT_IMPORTANCES = () => [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export default function RatingScaleQuestionEditor({
  field,
  colors,
  excludeAllFromScoring,
  t,
  onFieldChange,
}) {
  const includeInScore =
    !excludeAllFromScoring &&
    (field.hasImportance !== undefined ? field.hasImportance : true);

  const scaleImportances = Array.isArray(field.scaleImportances)
    ? [...field.scaleImportances]
    : DEFAULT_IMPORTANCES();

  while (scaleImportances.length < 11) {
    scaleImportances.push(scaleImportances.length);
  }
  if (scaleImportances.length > 11) scaleImportances.length = 11;

  const setImportanceAt = (idx, raw) => {
    const next = [...scaleImportances];
    if (typeof raw === "number" && !Number.isNaN(raw)) {
      next[idx] = Math.max(0, Math.floor(raw));
    } else {
      const digits = String(raw).replace(/\D/g, "");
      next[idx] = digits === "" ? 0 : Math.max(0, parseInt(digits, 10) || 0);
    }
    onFieldChange({ scaleImportances: next });
  };

  /** Olumlu uçta önem 10, karşı uca doğru 0’a iner (positiveEnd: min → 0→10, max → 10→0) */
  const applyAutoImportance = () => {
    const positive = field.positiveEnd === "max" ? "max" : "min";
    const next = Array.from({ length: 11 }, (_, i) =>
      positive === "min" ? 10 - i : i
    );
    onFieldChange({ scaleImportances: next });
  };

  const previewValue =
    typeof field._previewValue === "number"
      ? field._previewValue
      : 5;

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: colors.primary[600],
          border: `1px solid ${colors.primary[200]}33`,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: colors.grey[200], mb: 1 }}>
          {t("forms:ratingScaleEndpoints")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label={t("forms:ratingScaleMinLabel")}
              placeholder={t("forms:ratingScaleMinPlaceholder")}
              value={field.minLabel ?? ""}
              onChange={(e) => onFieldChange({ minLabel: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.primary[500],
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label={t("forms:ratingScaleMaxLabel")}
              placeholder={t("forms:ratingScaleMaxPlaceholder")}
              value={field.maxLabel ?? ""}
              onChange={(e) => onFieldChange({ maxLabel: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.primary[500],
                },
              }}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ color: colors.grey[200], mt: 2, mb: 1 }}>
          {t("forms:ratingScalePositiveSide")}
        </Typography>
        <RadioGroup
          row
          value={field.positiveEnd === "max" ? "max" : "min"}
          onChange={(e) => onFieldChange({ positiveEnd: e.target.value })}
          sx={{ gap: 1.5, flexWrap: "wrap" }}
        >
          {(["min", "max"]).map((end) => {
            const selected = (field.positiveEnd === "max" ? "max" : "min") === end;
            return (
              <FormControlLabel
                key={end}
                value={end}
                control={
                  <Radio
                    sx={{
                      color: colors.grey[500],
                      "&.Mui-checked": {
                        color: colors.greenAccent[400],
                      },
                    }}
                  />
                }
                label={t(
                  end === "min"
                    ? "forms:ratingScalePositiveMin"
                    : "forms:ratingScalePositiveMax"
                )}
                sx={{
                  m: 0,
                  py: 1.25,
                  px: 2,
                  pr: 2.5,
                  borderRadius: 2,
                  border: "2px solid",
                  borderColor: selected
                    ? colors.greenAccent[500]
                    : `${colors.grey[700]}`,
                  backgroundColor: selected
                    ? `${colors.greenAccent[900]}80`
                    : colors.primary[700],
                  transition: "background-color 0.2s, border-color 0.2s",
                  "&:hover": {
                    borderColor: selected
                      ? colors.greenAccent[400]
                      : colors.grey[500],
                    backgroundColor: selected
                      ? `${colors.greenAccent[900]}99`
                      : colors.primary[500],
                  },
                  "& .MuiFormControlLabel-label": {
                    color: colors.grey[100],
                    fontWeight: selected ? 700 : 500,
                    fontSize: "0.95rem",
                  },
                }}
              />
            );
          })}
        </RadioGroup>
        <Typography variant="caption" sx={{ color: colors.grey[400], display: "block" }}>
          {t("forms:ratingScalePositiveHint")}
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          backgroundColor: colors.primary[600],
          border: `1px solid ${colors.primary[200]}33`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ color: colors.grey[200], fontWeight: 600, mb: 0.5 }}>
            {t("forms:hasImportance")}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
            {excludeAllFromScoring
              ? t("forms:allQuestionsExcludedGlobal")
              : includeInScore
              ? t("forms:thisQuestionWillBeIncluded")
              : t("forms:thisQuestionWillNotBeIncluded")}
          </Typography>
        </Box>
        <Switch
          checked={excludeAllFromScoring ? false : includeInScore}
          onChange={(e) => {
            if (!excludeAllFromScoring) {
              onFieldChange({ hasImportance: e.target.checked });
            }
          }}
          disabled={excludeAllFromScoring}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: colors.greenAccent[500],
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: colors.greenAccent[500],
            },
            "& .Mui-disabled": {
              opacity: 0.5,
            },
          }}
        />
      </Box>

      {includeInScore && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: colors.grey[200], mb: 1 }}>
            {t("forms:ratingScaleImportanceByValue")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
            <Tooltip title={t("forms:ratingScaleAutoMatchHint")}>
              <Button
                size="small"
                variant="contained"
                onClick={applyAutoImportance}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: colors.grey[100],
                  backgroundColor: colors.blueAccent[700],
                  border: `1px solid ${colors.blueAccent[400]}`,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: colors.blueAccent[600],
                    boxShadow: `0 2px 8px ${colors.blueAccent[900]}80`,
                  },
                }}
              >
                {t("forms:ratingScaleAutoMatch")}
              </Button>
            </Tooltip>
          </Box>
          <Grid container spacing={1.5}>
            {scaleImportances.map((imp, vi) => (
              <Grid item xs={6} sm={4} md={3} key={vi}>
                <TextField
                  size="small"
                  fullWidth
                  type="text"
                  inputMode="numeric"
                  label={t("forms:ratingScaleValueImportance", { value: vi })}
                  value={String(imp)}
                  onChange={(e) => setImportanceAt(vi, e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: colors.primary[500],
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: colors.primary[600],
          border: `1px solid ${colors.primary[200]}22`,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: colors.grey[300], mb: 1 }}>
          {t("forms:preview")}
        </Typography>
        <RatingScaleSlider
          value={previewValue}
          onChange={(n) => onFieldChange({ _previewValue: n })}
          minLabel={field.minLabel || "0"}
          maxLabel={field.maxLabel || "10"}
          positiveEnd={field.positiveEnd === "max" ? "max" : "min"}
          colors={colors}
        />
      </Box>
    </Box>
  );
}
